import type {
  ExperienceSettings,
  QualityLevel,
  TimePreset,
  WeatherKind,
} from "../core/types";
import { hasTouchInput } from "../core/device";
import { cloneSettings, createSeed, normalizeSettings, sanitizeSeed } from "./settings";
import type {
  ExperienceUICallbacks,
  ExperienceUIOptions,
  SaveSummary,
  StartRequest,
} from "./types";

const WEATHER_OPTIONS: readonly [WeatherKind, string][] = [
  ["clear", "晴"],
  ["cloudy", "少云"],
  ["overcast", "阴"],
  ["mist", "薄雾"],
  ["drizzle", "细雨"],
  ["after-rain", "雨后"],
];
const TIME_OPTIONS: readonly [TimePreset, string][] = [
  ["dawn", "破晓"],
  ["morning", "清晨"],
  ["noon", "午后"],
  ["golden-hour", "黄昏"],
  ["night", "夜晚"],
];
const QUALITY_OPTIONS: readonly [QualityLevel, string][] = [
  ["medium", "中"],
  ["high", "高"],
];

export class ExperienceUI {
  private readonly root: HTMLElement;
  private readonly callbacks: ExperienceUICallbacks;
  private readonly abortController = new AbortController();
  private settings: ExperienceSettings;
  private readonly startSurface: HTMLElement;
  private readonly pauseSurface: HTMLElement;
  private readonly hintSurface: HTMLElement;
  private readonly interactionSurface: HTMLElement;
  private readonly errorSurface: HTMLElement;
  private readonly seedInput: HTMLInputElement;
  private readonly startButton: HTMLButtonElement;
  private readonly loadButton: HTMLButtonElement | null;
  private readonly savedSeed: string | null;
  private hintTimeout: number | null = null;
  private controlsHintShown = false;
  private readonly touchDevice: boolean;
  private started = false;
  private disposed = false;

  constructor(options: ExperienceUIOptions) {
    this.root = options.root;
    this.callbacks = options.callbacks;
    this.touchDevice = hasTouchInput();
    this.settings = normalizeSettings(options.settings);
    this.savedSeed = options.save ? sanitizeSeed(options.save.seed) : null;
    this.root.classList.add("experience-root");

    this.startSurface = this.buildStartSurface(
      options.save ?? null,
      options.initialSeed ?? createSeed(),
      options.isDesktopCompatible ?? matchMedia("(min-width: 900px)").matches,
    );
    this.pauseSurface = this.buildPauseSurface();
    this.hintSurface = this.makeLiveRegion("controls-hint", "status");
    this.interactionSurface = this.makeLiveRegion("interaction-hint", "status");
    this.errorSurface = this.makeLiveRegion("error-surface", "alert");
    this.root.append(
      this.startSurface,
      this.pauseSurface,
      this.hintSurface,
      this.interactionSurface,
      this.errorSurface,
    );

    const seedInput = this.startSurface.querySelector<HTMLInputElement>("#world-seed");
    const startButton = this.startSurface.querySelector<HTMLButtonElement>("[data-action='new']");
    if (!seedInput || !startButton) throw new Error("Start surface controls were not created.");
    this.seedInput = seedInput;
    this.startButton = startButton;
    this.loadButton = this.startSurface.querySelector<HTMLButtonElement>("[data-action='load']");

    this.bindEvents();
  }

  showStart(): void {
    this.started = false;
    this.hide(this.pauseSurface);
    this.show(this.startSurface);
    this.seedInput.focus();
  }

  showPause(): void {
    if (!this.started) return;
    this.hide(this.interactionSurface);
    this.show(this.pauseSurface);
    this.pauseSurface.querySelector<HTMLElement>("[data-action='resume']")?.focus();
  }

  hidePause(): void {
    this.hide(this.pauseSurface);
  }

  showControlsHint(): void {
    if (this.controlsHintShown || !this.settings.comfort.interactionHints) return;
    this.controlsHintShown = true;
    if (this.touchDevice) return;
    this.hintSurface.innerHTML = "<p><strong>WASD</strong> 行走 · <strong>Shift</strong> 慢跑 · <strong>C</strong> 蹲下 · <strong>E</strong> 互动 · <strong>Esc</strong> 暂停</p>";
    this.show(this.hintSurface);
    if (this.hintTimeout !== null) window.clearTimeout(this.hintTimeout);
    this.hintTimeout = window.setTimeout(() => this.hide(this.hintSurface), 6500);
  }

  setInteractionHint(label: string | null): void {
    if (!label || !this.settings.comfort.interactionHints || !this.started) {
      this.hide(this.interactionSurface);
      return;
    }
    this.interactionSurface.textContent = `按 E ${label}`;
    this.show(this.interactionSurface);
  }

  setSettings(settings: ExperienceSettings): void {
    this.settings = normalizeSettings(settings);
    this.syncSettingsControls();
    if (!this.settings.comfort.interactionHints) {
      this.hide(this.hintSurface);
      this.hide(this.interactionSurface);
    }
  }

  showWebGLError(message = "此浏览器未能启动 WebGL2 场景。请更新 Chrome 或 Edge，并确认硬件加速已开启。"): void {
    this.errorSurface.innerHTML = `
      <div class="error-content">
        <p class="surface-eyebrow">场景未能打开</p>
        <h1>无法进入这段散步</h1>
        <p>${escapeHTML(message)}</p>
        <button type="button" class="primary-action" data-action="reload">重新加载场景</button>
      </div>`;
    this.hide(this.startSurface);
    this.hide(this.pauseSurface);
    this.show(this.errorSurface);
    this.errorSurface.querySelector<HTMLButtonElement>("[data-action='reload']")?.focus();
  }

  clearError(): void {
    this.hide(this.errorSurface);
  }

  dispose(): void {
    if (this.disposed) return;
    this.disposed = true;
    this.abortController.abort();
    if (this.hintTimeout !== null) window.clearTimeout(this.hintTimeout);
    this.startSurface.remove();
    this.pauseSurface.remove();
    this.hintSurface.remove();
    this.interactionSurface.remove();
    this.errorSurface.remove();
  }

  private buildStartSurface(
    save: SaveSummary | null,
    initialSeed: string,
    isDesktopCompatible: boolean,
  ): HTMLElement {
    const surface = document.createElement("section");
    surface.className = "experience-surface start-surface";
    surface.setAttribute("aria-labelledby", "start-title");
    surface.innerHTML = `
      <div class="start-content">
        <p class="surface-eyebrow">江南 · 晚春雨后</p>
        <h1 id="start-title">听溪</h1>
        <p class="start-subtitle">从临河小院，慢慢走进清晨。</p>
        <form class="seed-form">
          <label for="world-seed">世界种子</label>
          <div class="seed-row">
            <input id="world-seed" name="seed" type="text" maxlength="64" autocomplete="off" spellcheck="false" value="${escapeHTML(sanitizeSeed(initialSeed))}" />
            <button type="button" class="quiet-action" data-action="randomize">换一个</button>
          </div>
          <div class="start-actions">
            <button type="submit" class="primary-action" data-action="new">开始散步</button>
            ${save ? `<button type="button" class="secondary-action" data-action="load">继续上次散步</button>` : ""}
          </div>
        </form>
        ${save ? `<p class="save-note">上次停在种子 ${escapeHTML(save.seed)} · ${formatSaveTime(save.updatedAt)}</p>` : ""}
        ${isDesktopCompatible ? "" : '<p class="compatibility-notice" role="note">已启用手机触控：左侧摇杆移动，右侧拖动视角。</p>'}
      </div>`;
    return surface;
  }

  private buildPauseSurface(): HTMLElement {
    const surface = document.createElement("section");
    surface.className = "experience-surface pause-surface is-hidden";
    surface.setAttribute("aria-labelledby", "pause-title");
    surface.setAttribute("aria-modal", "true");
    surface.setAttribute("role", "dialog");
    surface.innerHTML = `
      <div class="pause-layout">
        <header class="pause-heading">
          <p class="surface-eyebrow">散步暂停</p>
          <h1 id="pause-title">院外的风还在吹</h1>
          <div class="pause-actions">
            <button type="button" class="primary-action" data-action="resume">继续散步</button>
            <button type="button" class="quiet-action" data-action="home">回到落脚处</button>
          </div>
        </header>
        <form class="settings-form" aria-label="场景设置">
          ${segmented("时间", "timePreset", TIME_OPTIONS)}
          ${toggle("让时间缓慢流逝", "timeFlow")}
          ${segmented("天气", "weather", WEATHER_OPTIONS)}
          <div class="settings-pair">
            ${toggle("居民", "residents")}
            ${toggle("动物", "animals")}
          </div>
          ${segmented("画质", "quality", QUALITY_OPTIONS)}
          ${range("声音", "masterVolume", 0, 1, 0.05)}
          ${range("视野", "fov", 55, 85, 1)}
          <div class="settings-pair comfort-group">
            ${toggle("镜头起伏", "headBob", true)}
            ${toggle("动态模糊", "motionBlur", true)}
            ${toggle("互动提示", "interactionHints", true)}
          </div>
        </form>
      </div>`;
    return surface;
  }

  private makeLiveRegion(className: string, role: string): HTMLElement {
    const region = document.createElement("div");
    region.className = `${className} is-hidden`;
    region.setAttribute("role", role);
    region.setAttribute("aria-live", role === "alert" ? "assertive" : "polite");
    return region;
  }

  private bindEvents(): void {
    const signal = this.abortController.signal;
    this.startSurface.addEventListener(
      "submit",
      (event) => {
        event.preventDefault();
        void this.begin({ mode: "new", seed: sanitizeSeed(this.seedInput.value) || createSeed() });
      },
      { signal },
    );
    this.startSurface.addEventListener(
      "click",
      (event) => {
        const action = (event.target as Element).closest<HTMLElement>("[data-action]")?.dataset.action;
        if (action === "randomize") this.seedInput.value = createSeed();
        if (action === "load" && this.loadButton && this.savedSeed) {
          void this.begin({ mode: "load", seed: this.savedSeed });
        }
      },
      { signal },
    );
    this.pauseSurface.addEventListener(
      "click",
      (event) => {
        const action = (event.target as Element).closest<HTMLElement>("[data-action]")?.dataset.action;
        if (action === "resume") void this.resume();
        if (action === "home") void this.returnHome();
      },
      { signal },
    );
    this.pauseSurface.addEventListener("input", this.handleSettingsInput, { signal });
    this.errorSurface.addEventListener(
      "click",
      (event) => {
        const action = (event.target as Element).closest<HTMLElement>("[data-action]")?.dataset.action;
        if (action === "reload") {
          if (this.callbacks.onReload) this.callbacks.onReload();
          else location.reload();
        }
      },
      { signal },
    );
    window.addEventListener(
      "keydown",
      (event) => {
        if (
          event.code !== "Escape" ||
          !this.started ||
          this.pauseSurface.classList.contains("is-hidden")
        ) {
          return;
        }
        event.preventDefault();
        void this.resume();
      },
      { signal },
    );
    this.syncSettingsControls();
  }

  private async begin(request: StartRequest): Promise<void> {
    this.setStartBusy(true);
    try {
      await this.callbacks.onStart(request);
      this.started = true;
      this.hide(this.startSurface);
      this.showControlsHint();
    } catch (error) {
      this.showWebGLError(
        error instanceof Error
          ? error.message
          : "场景载入中断，请重新加载后再试。",
      );
    } finally {
      this.setStartBusy(false);
    }
  }

  private async resume(): Promise<void> {
    await this.callbacks.onResume();
    this.hidePause();
  }

  private async returnHome(): Promise<void> {
    await this.callbacks.onReturnHome();
    await this.resume();
  }

  private readonly handleSettingsInput = (event: Event): void => {
    const control = event.target as HTMLInputElement;
    const name = control.name;
    const value: unknown = control.type === "checkbox" ? control.checked : control.value;
    const next = cloneSettings(this.settings);
    if (name === "weather") next.weather = value as WeatherKind;
    if (name === "timePreset") next.timePreset = value as TimePreset;
    if (name === "quality") next.quality = value as QualityLevel;
    if (name === "timeFlow") next.timeFlow = Boolean(value);
    if (name === "residents") next.residents = Boolean(value);
    if (name === "animals") next.animals = Boolean(value);
    if (name === "masterVolume") next.masterVolume = Number(value);
    if (name === "fov") next.comfort.fov = Number(value);
    if (name === "headBob") next.comfort.headBob = Boolean(value);
    if (name === "motionBlur") next.comfort.motionBlur = Boolean(value);
    if (name === "interactionHints") next.comfort.interactionHints = Boolean(value);
    this.setSettings(next);
    this.callbacks.onSettingsChange(cloneSettings(this.settings));
  };

  private syncSettingsControls(): void {
    const values: Record<string, string | boolean | number> = {
      weather: this.settings.weather,
      timePreset: this.settings.timePreset,
      timeFlow: this.settings.timeFlow,
      residents: this.settings.residents,
      animals: this.settings.animals,
      quality: this.settings.quality,
      masterVolume: this.settings.masterVolume,
      fov: this.settings.comfort.fov,
      headBob: this.settings.comfort.headBob,
      motionBlur: this.settings.comfort.motionBlur,
      interactionHints: this.settings.comfort.interactionHints,
    };
    for (const control of this.pauseSurface.querySelectorAll<HTMLInputElement>("input")) {
      const value = values[control.name];
      if (control.type === "checkbox") control.checked = Boolean(value);
      else if (control.type === "radio") control.checked = control.value === value;
      else control.value = String(value);
    }
    for (const output of this.pauseSurface.querySelectorAll<HTMLOutputElement>("output[data-for]")) {
      const value = values[output.dataset.for ?? ""];
      output.value = output.dataset.for === "masterVolume" ? `${Math.round(Number(value) * 100)}%` : `${value}°`;
    }
  }

  private setStartBusy(busy: boolean): void {
    this.startButton.disabled = busy;
    if (this.loadButton) this.loadButton.disabled = busy;
    this.startSurface.setAttribute("aria-busy", String(busy));
  }

  private show(element: HTMLElement): void {
    element.classList.remove("is-hidden");
  }

  private hide(element: HTMLElement): void {
    element.classList.add("is-hidden");
  }
}

function segmented<T extends string>(
  label: string,
  name: string,
  options: readonly (readonly [T, string])[],
): string {
  return `<fieldset class="setting-row segmented-setting"><legend>${label}</legend><div class="segmented-control">${options
    .map(
      ([value, text]) =>
        `<label><input type="radio" name="${name}" value="${value}" /><span>${text}</span></label>`,
    )
    .join("")}</div></fieldset>`;
}

function toggle(label: string, name: string, comfort = false): string {
  return `<label class="setting-row toggle-setting"><span>${label}</span><input type="checkbox" name="${name}" ${comfort ? 'data-group="comfort"' : ""} /><i aria-hidden="true"></i></label>`;
}

function range(
  label: string,
  name: string,
  min: number,
  max: number,
  step: number,
): string {
  return `<label class="setting-row range-setting"><span>${label}</span><input type="range" name="${name}" min="${min}" max="${max}" step="${step}" /><output data-for="${name}"></output></label>`;
}

function formatSaveTime(timestamp: number): string {
  if (!Number.isFinite(timestamp)) return "可继续";
  return new Intl.DateTimeFormat("zh-CN", {
    month: "numeric",
    day: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  }).format(timestamp);
}

function escapeHTML(value: string): string {
  return value.replace(
    /[&<>'"]/g,
    (character) =>
      ({ "&": "&amp;", "<": "&lt;", ">": "&gt;", "'": "&#39;", '"': "&quot;" })[
        character
      ] ?? character,
  );
}
